import {
	FormList,
	FormShape,
	type GetFormInput,
	type GetFormInputSetter,
	type GetFormOutputVerbose,
} from "@suse-o11y/signal/form";

import { isDeepEqual } from "~/tools/is-deep-equal";
import { readonly } from "~/tools/readonly";

import type { WidgetFormSync } from "../_sync";
import { FormState as CalculationFormState } from "../calculation/form-state";
import { FormState as ColorFormState } from "../color/form-state";
import { CRITICAL as CRITICAL_COLOR, WARNING as WARNING_COLOR } from "../color/settings";
import { FormState as DecimalsFormState } from "../decimals/form-state";
import { FormState as MinMaxFormState } from "../min-max/form-state";
import {
	ModeFormState as ThresholdsModeFormState,
	StepFormState as ThresholdsStepFormState,
} from "../thresholds/form-state";
import { FormState as UnitFormState } from "../unit/form-state";

import { DEFAULT as DEFAULT_SETTINGS, type Settings } from "./settings";

type FormState = FormShape<{
	unit: UnitFormState;
	decimals: DecimalsFormState;
	max: MinMaxFormState;
	calculation: CalculationFormState;
	color: ColorFormState;

	thresholds: FormShape<{
		mode: ThresholdsModeFormState;
		steps: FormList<ThresholdsStepFormState>;
	}>;
}>;

const INITIAL: GetFormInput<FormState> = {
	unit: UnitFormState.initial,
	decimals: DecimalsFormState.initial,
	max: MinMaxFormState.initial,
	calculation: DEFAULT_SETTINGS.calculation,
	color: DEFAULT_SETTINGS.color,
	thresholds: {
		mode: DEFAULT_SETTINGS.thresholds.mode,
		steps: [
			{ value: "0.9", color: CRITICAL_COLOR },
			{ value: "0.8", color: WARNING_COLOR },
		],
	},
};

function fromSettings(settings: undefined | Settings) {
	return {
		unit: settings?.unit,
		decimals: settings?.decimals?.toString(),
		max: settings?.max?.toString(),
		calculation: settings?.calculation?.toString(),
		color: settings?.color,
		thresholds: {
			mode: settings?.thresholds?.mode?.toString(),
			steps: readonly(settings?.thresholds?.steps?.map(ThresholdsStepFormState.fromSettings)),
		},
	} satisfies GetFormInputSetter<FormState>;
}

type Input = ReturnType<typeof fromSettings>;

function init(initial: Input): FormState {
	const steps = initial.thresholds.steps?.length ? initial.thresholds.steps : INITIAL.thresholds.steps;

	return FormShape({
		unit: UnitFormState.init(initial.unit ?? INITIAL.unit),
		decimals: DecimalsFormState.init(initial.decimals),
		max: MinMaxFormState.init(initial.max),
		calculation: CalculationFormState.init(initial.calculation ?? INITIAL.calculation),
		color: ColorFormState.init(initial.color ?? INITIAL.color),
		thresholds: FormShape({
			mode: ThresholdsModeFormState.init(initial.thresholds.mode ?? INITIAL.thresholds.mode),
			steps: FormList(ThresholdsStepFormState.init, {
				initial: steps,
			}),
		}),
	});
}

function toSettings(output: GetFormOutputVerbose<FormState>): Settings {
	return {
		unit: output.unit ?? undefined,
		decimals: output.decimals ?? undefined,
		max: output.max ?? undefined,
		calculation: output.calculation ?? undefined,
		color: output.color ?? undefined,
		thresholds: {
			mode: output.thresholds.mode ?? undefined,
			steps: output.thresholds.steps.map((step) => ({
				color: step.color ?? undefined,
				value: step.value ?? undefined,
			})),
		},
	};
}

function fromSync({ unit, decimals, max, calculation, color, thresholds }: WidgetFormSync): Input {
	return {
		unit,
		decimals,
		max,
		calculation,
		color,
		thresholds: {
			mode: thresholds?.mode,
			steps: thresholds?.steps?.length ? thresholds.steps : INITIAL.thresholds.steps,
		},
	};
}

function toSync({ unit, decimals, max, calculation, color, thresholds }: Input): WidgetFormSync {
	return {
		unit,
		decimals,
		max,
		calculation,
		color,
		thresholds: {
			mode: thresholds?.mode,
			steps: isDeepEqual(thresholds.steps, INITIAL.thresholds.steps) ? [] : thresholds?.steps,
		},
	};
}

const FormState = {
	initial: INITIAL,
	init,
	fromSettings,
	toSettings,
	toSync,
	fromSync,
} as const;

export { FormState };
